// Browser Web Speech API (webkitSpeechRecognition). Single utterance per tap;
// interim results shown live in the composer. Hidden when unsupported.

import { useEffect, useRef, useState } from "react";

export interface SpeechInputHook {
  supported: boolean;
  isRecording: boolean;
  interimText: string;
  start: (onFinal: (text: string) => void) => void;
  stop: () => void;
}

export function useSpeechInput(): SpeechInputHook {
  const [supported] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return "webkitSpeechRecognition" in window;
  });
  const [isRecording, setIsRecording] = useState(false);
  const [interimText, setInterimText] = useState("");
  const recogRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    return () => {
      recogRef.current?.abort();
    };
  }, []);

  function start(onFinal: (text: string) => void) {
    if (!supported || isRecording) return;
    const SR = window.webkitSpeechRecognition;
    if (!SR) return;
    const recog = new SR();
    recog.continuous = false;
    recog.interimResults = true;
    recog.lang = "en-US";
    recogRef.current = recog;

    recog.onresult = (e) => {
      const result = e.results[e.results.length - 1];
      if (!result) return;
      const transcript = result[0]?.transcript ?? "";
      if (result.isFinal) {
        setInterimText("");
        onFinal(transcript.trim());
      } else {
        setInterimText(transcript);
      }
    };

    recog.onend = () => {
      setIsRecording(false);
      setInterimText("");
      recogRef.current = null;
    };

    recog.onerror = () => {
      setIsRecording(false);
      setInterimText("");
      recogRef.current = null;
    };

    setIsRecording(true);
    recog.start();
  }

  function stop() {
    recogRef.current?.stop();
    setIsRecording(false);
    setInterimText("");
    recogRef.current = null;
  }

  return { supported, isRecording, interimText, start, stop };
}
